import { AsyncPipe,CommonModule } from '@angular/common';
import { ChangeDetectionStrategy,Component,Input,OnDestroy } from '@angular/core';
import { FormControl,FormGroup,ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject,combineLatest,firstValueFrom,Observable,of,Subject,switchMap,tap } from 'rxjs';

import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { LegacyPageEvent as PageEvent,MatLegacyPaginator as MatPaginator,MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MaterialAmountType } from '@dave/types/dist/proto/erp/warehouseMovements';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Actions } from '@ngrx/effects';
import { Dictionary } from '@ngrx/entity';
import { distinctUntilChanged,map,shareReplay,startWith,takeUntil } from 'rxjs/operators';
import { CommissionEntity } from '../../../dave-data-module/entities/commission.entity';
import { MaterialEntity } from '../../../dave-data-module/entities/material.entity';
import { QuantityTypeEntity } from '../../../dave-data-module/entities/quantity-type.entity';
import { getFetched$ } from '../../../dave-data-module/helper/helper';
import { MaterialDataService } from '../../../dave-data-module/services/material-data.service';
import { PaginatedMaterialService } from '../../../dave-data-module/services/paginated-material.service';
import { State } from '../../../dave-data-module/State';
import { getAccountsReceivableLedgersFetched,getQuantityTypeDictionary,getQuantityTypesFetched } from '../../../dave-data-module/State/selectors/accounting.selector';
import { getCommissionById,getCommissionsFetched } from '../../../dave-data-module/State/selectors/commission.selector';
import { getLedgerImportsFetched } from '../../../dave-data-module/State/selectors/ledger-import.selector';
import { DaveLoadingModule } from '../../../dave-loading/dave-loading.module';
import { AppButtonModule } from '../../../dave-utils-module/app-button-module/app-button.module';
import { SelectSearchPagedOptionsComponent } from '../../../dave-utils-module/select-search/components/select-search-paged-options/select-search-paged-options.component';
import { SelectSearchOption } from '../../../dave-utils-module/select-search/components/select-search/select-search.component';
import { SelectSearchModule } from '../../../dave-utils-module/select-search/select-search.module';
import { isNotNullOrUndefined,uniqArray } from '../../../helper/helper';
import { MaterialMainDataPopupComponent,MaterialMainDataPopupComponentDialogData } from '../../../resource-dispo/components/material-main-data-popup/material-main-data-popup.component';
import { CustomLabelService } from '../../../services/custom-label/custom-label.service';
import {
SelectWarehouseMovementTypeDialogComponent,
SelectWarehouseMovementTypeDialogComponentDialogData,
SelectWarehouseMovementTypeDialogComponentDialogReturnData
} from '../select-warehouse-movement-type-dialog/select-warehouse-movement-type-dialog.component';

interface materialListItem {
    warn$: Observable<boolean>;
    number: string;
    name: string;
    quantityFromWarehouse: number;
    quantityTarget: number;
    quantityType: string;
    quantityTypeId: number;
    colorClass$: Observable<'green' | 'yellow' | null>;
    disabled: boolean;
    hide: boolean;
    quantityFormGroup: FormGroup<{ quantity: FormControl<number>; materialId: FormControl<MaterialEntity> }>;
    arlId: number | null;
    commissionId: number | null;
    searchValues: string[];
    warehouseMovementType: MaterialAmountType.MaterialAmountTypeInput | MaterialAmountType.MaterialAmountTypeOutput;
    warehouseMovementTypeLabel: string;
    commission$: Observable<CommissionEntity | null> | null;
    detectedCommissionNumber: string;
    _destroy$: Subject<void>;
}
type EntryValues = {
    Quantity: number;
    BookingText: string;
    Information: string;
    MaterialId: number;
    arlId?: number;
    QuantityTypeId: number;
    CommissionId: number | null;
    warehouseMovementType: MaterialAmountType.MaterialAmountTypeInput | MaterialAmountType.MaterialAmountTypeOutput;
    detectedCommissionNumber: string;
};
@Component({
    selector: 'app-material-for-warehouse-movement-list',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        FontAwesomeModule,
        AppButtonModule,
        AsyncPipe,
        MatInputModule,
        ReactiveFormsModule,
        MatTooltipModule,
        SelectSearchModule,
        DaveLoadingModule,
        MatPaginatorModule,
        SelectSearchPagedOptionsComponent,
    ],
    templateUrl: './material-for-warehouse-movement-list.component.html',
    styleUrls: ['./material-for-warehouse-movement-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [PaginatedMaterialService],
})
export class MaterialForWarehouseMovementListComponent implements OnDestroy {
    private startEntrys$ = new BehaviorSubject<EntryValues[]>(null);

    @Input() set startEntrys(v: EntryValues[]) {
        if (v !== this.startEntrys$.value) {
            this.startEntrys$.next(v);
        }
    }
    @Input() set defaultAddedMaterials(v: { materialId: number; warehouseMovementType: MaterialAmountType.MaterialAmountTypeInput | MaterialAmountType.MaterialAmountTypeOutput }[] | null) {
        if (v?.length) {
            v.forEach(({ materialId, warehouseMovementType }) => {
                this.addEmptyMaterial(materialId, warehouseMovementType);
            });
        }
    }

    private destroy$ = new Subject<void>();

    protected showTypeColumn$: Observable<boolean>;
    protected materials$: Observable<materialListItem[]>;
    protected addedMaterials$: BehaviorSubject<materialListItem[]>;

    protected materialList$: Observable<materialListItem[]>;
    protected materialListFiltered$: Observable<materialListItem[]>;
    protected searchString$: BehaviorSubject<string>;
    protected materialOptions$ = this._materialPageService.selectItems$;
    protected loading$: Observable<boolean>;
    private readonly quantityTypes$: Observable<Dictionary<QuantityTypeEntity>>;
    compareById = (a: SelectSearchOption<{ Id: number }>, b: SelectSearchOption<{ Id: number }>) => a?.Id === b?.Id;

    constructor(
        private store: Store<State>,
        private actions$: Actions,
        private dialog: MatDialog,
        private materialDataService: MaterialDataService,
        protected _materialPageService: PaginatedMaterialService,
        protected cls: CustomLabelService,
    ) {
        this.searchString$ = new BehaviorSubject('');
        this.loading$ = combineLatest([store.select(getCommissionsFetched), store.select(getQuantityTypesFetched), store.select(getLedgerImportsFetched), store.select(getAccountsReceivableLedgersFetched)]).pipe(
            map((fetchers) => fetchers.some((f) => !f)),
        );
        this.addedMaterials$ = new BehaviorSubject<materialListItem[]>([]);
        this.quantityTypes$ = getFetched$(store, getQuantityTypesFetched, getQuantityTypeDictionary);
        this.materials$ = this.startEntrys$.pipe(
            switchMap((entries) => {
                // const arlsFromLedgerImport = ledgerImport ? ledgerImport.AccountsReceivableLedgerIds.map((arlId) => _getArlChildren(arlId, arls, true)).flat() : [];
                const materialIds = uniqArray(entries.map((arl) => arl.MaterialId).filter(isNotNullOrUndefined));
                return combineLatest([this.quantityTypes$, materialIds.length ? this.materialDataService.getMaterialsById$(materialIds, false) : of([] as MaterialEntity[])]).pipe(
                    map(([quantityTypes, materials]) => {
                        return entries.map<materialListItem>((e) => {
                            const material = e.MaterialId && materials.find((m) => m.Id === e.MaterialId);
                            return {
                                ...this.getValuesFromMaterialEntity(quantityTypes, e.warehouseMovementType, material, e),
                                disabled: false, // todo
                                hide: false,
                                arlId: e.arlId,
                                quantityTypeId: e.QuantityTypeId,
                                commissionId: e.CommissionId,
                                commission$: e.CommissionId && getFetched$(store, getCommissionsFetched, getCommissionById({ id: e.CommissionId })),
                                detectedCommissionNumber: e.detectedCommissionNumber,
                                _destroy$: new Subject<void>(),
                            };
                        });
                    }),
                );
            }),
        );
        // this.materials$ = this.ledgerImportId$.pipe(
        //     // filter(isNotNullOrUndefined),
        //     switchMap((liId) => (liId ? getFetched$(store, getLedgerImportsFetched, getLedgerImportById({ id: liId })) : of(null))),
        //     switchMap((ledgerImport) =>
        //         getFetched$(store, getAccountsReceivableLedgersFetched, getAccountsReceivableLedgerDictionary).pipe(
        //             switchMap((arls) => {
        //                 const arlsFromLedgerImport = ledgerImport ? ledgerImport.AccountsReceivableLedgerIds.map((arlId) => _getArlChildren(arlId, arls, true)).flat() : [];
        //                 const materialIds = uniqArray(arlsFromLedgerImport.map((arl) => arl.MaterialId).filter(isNotNullOrUndefined));
        //                 return combineLatest([this.quantityTypes$, materialIds.length ? this.materialDataService.getMaterialsById$(materialIds, false) : of([] as MaterialEntity[])]).pipe(
        //                     map(([quantityTypes, materials]) => {
        //                         return arlsFromLedgerImport.map<materialListItem>((arl) => {
        //                             const material = arl.MaterialId && materials.find((m) => m.Id === arl.MaterialId);
        //                             return {
        //                                 ...this.getValuesFromMaterialEntity(quantityTypes, material, arl),
        //                                 disabled: false, // todo
        //                                 hide: false,
        //                                 arlId: arl.Id,
        //                                 quantityTypeId: arl.QuantityTypeId,
        //                             };
        //                         });
        //                     }),
        //                 );
        //             }),
        //         ),
        //     ),
        // );
        this.materialList$ = combineLatest([this.materials$.pipe(tap((x) => console.log('materialList$', x))), this.addedMaterials$.pipe(tap((x) => console.log('addedMaterials$', x)))]).pipe(
            map(([fromLi, added]) => {
                return [...fromLi, ...added].filter(isNotNullOrUndefined);
            }),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        this.materialListFiltered$ = this.materialList$.pipe(
            switchMap((materials) =>
                this.searchString$.pipe(
                    map((search) => {
                        if (search) {
                            const searchString = search.toLowerCase();
                            return materials.filter((m) => m.searchValues.some((s) => s.includes(searchString)));
                        } else {
                            return materials;
                        }
                    }),
                ),
            ),
        );
        this.showTypeColumn$ = this.materialList$.pipe(map((materials) => materials.every((m) => m.warehouseMovementType && m.warehouseMovementType === materials[0].warehouseMovementType)));
    }

    ngOnDestroy(): void {
        this.addedMaterials$.value.forEach((m) => {
            m._destroy$.next()
            m._destroy$.complete()
        });

        this.destroy$.next();
        this.destroy$.complete();

        this.searchString$.complete();
        this.addedMaterials$.complete();
    }

    materialOptionsSearchChange(searchQuery: string, matPaginator: MatPaginator) {
        this._materialPageService.setSearch(searchQuery).then(() => {
            this._materialPageService.loadPage({ offset: 0 });
            matPaginator.pageIndex = 0;
        });
    }

    materialOptionsPageChange(page: PageEvent) {
        this._materialPageService.loadPage({
            offset: page.pageIndex * page.pageSize,
        });
    }
    onTimesClick(material: materialListItem) {
        material.disabled = true;
        material._destroy$.next();
        material._destroy$.complete();
        this.addedMaterials$.next(this.addedMaterials$.value.filter((m) => m !== material));
    }

    onCheckClick(material: materialListItem) {
        material.disabled = false;
    }
    deleteMaterial(material: materialListItem) {
        material.hide = true;
    }
    async addEntry(entry: EntryValues) {
        let material: MaterialEntity;
        if (entry.MaterialId) {
            material = await firstValueFrom(this.materialDataService.getMaterialById$(entry.MaterialId));
        }
        const quantityTypes = await firstValueFrom(this.quantityTypes$);

        const newItem: materialListItem = {
            ...this.getValuesFromMaterialEntity(quantityTypes, entry.warehouseMovementType, material, entry),
            disabled: false, // todo
            hide: false,
            arlId: entry.arlId,
            quantityTypeId: entry.QuantityTypeId,
            commissionId: entry.CommissionId,
            commission$: entry.CommissionId && getFetched$(this.store, getCommissionsFetched, getCommissionById({ id: entry.CommissionId })),
            detectedCommissionNumber: entry.detectedCommissionNumber,
            _destroy$: new Subject<void>(),
        };
        newItem.quantityFormGroup.controls.materialId.valueChanges.pipe(takeUntil(this.destroy$), takeUntil(newItem._destroy$)).subscribe((m) => {
            if (m?.Id) {
                firstValueFrom(this.materialDataService.getMaterialById$(m.Id)).then((material) => {
                    this.updateMaterialValues(newItem, material);
                });
            }
        });
        this.addedMaterials$.next([...this.addedMaterials$.value, newItem]);
    }
    async addEmptyMaterial(materialId?: number, warehouseMovementType?: MaterialAmountType.MaterialAmountTypeInput | MaterialAmountType.MaterialAmountTypeOutput) {
        let wtype = warehouseMovementType;
        if (!wtype) {
            const ret = await firstValueFrom(
                this.dialog
                    .open<SelectWarehouseMovementTypeDialogComponent, SelectWarehouseMovementTypeDialogComponentDialogData, SelectWarehouseMovementTypeDialogComponentDialogReturnData>(SelectWarehouseMovementTypeDialogComponent, {
                        ...SelectWarehouseMovementTypeDialogComponent.DefaultConfig,
                        disableClose: true,
                    })
                    .afterClosed(),
            );
            if (ret) {
                wtype = ret === 'in' ? MaterialAmountType.MaterialAmountTypeInput : MaterialAmountType.MaterialAmountTypeOutput;
            }
        }
        if (wtype) {
            await this.addEntry({ MaterialId: materialId, BookingText: null, Quantity: null, QuantityTypeId: null, Information: null, CommissionId: null, warehouseMovementType: wtype, detectedCommissionNumber: null });
        }
    }
    getValuesFromMaterialEntity(
        quantityTypes: Dictionary<QuantityTypeEntity>,
        warehouseMovementType: MaterialAmountType.MaterialAmountTypeInput | MaterialAmountType.MaterialAmountTypeOutput,
        material?: MaterialEntity,
        arl?: { Quantity: number; BookingText: string; Information: string; QuantityTypeId: number },
    ): Pick<materialListItem, 'number' | 'name' | 'quantityFromWarehouse' | 'colorClass$' | 'quantityType' | 'quantityTarget' | 'quantityFormGroup' | 'warn$' | 'searchValues' | 'warehouseMovementType' | 'warehouseMovementTypeLabel'> {
        const quantityWarehouse = material?.Amount || 0;
        const targetQuantity = arl?.Quantity;

        const quantityFormGroup = new FormGroup({
            quantity: new FormControl<number>(targetQuantity),
            materialId: new FormControl<MaterialEntity>(material?.Id ? material : null),
        });

        return {
            number: material ? material.ArticleNumber || '' : arl?.BookingText || '',
            name: material ? material.Name || '' : arl?.Information || '',
            quantityFromWarehouse: quantityWarehouse,
            quantityTarget: targetQuantity,
            quantityFormGroup,
            searchValues: [material?.Name, material?.ArticleNumber, material?.InventoryNumber, material?.GlobalTradeItemNr, arl?.BookingText, arl?.Information].filter((v) => v).map((v) => v.toLowerCase()),
            warehouseMovementType,
            warehouseMovementTypeLabel: warehouseMovementType ? (warehouseMovementType === MaterialAmountType.MaterialAmountTypeInput ? 'Eingang' : 'Ausgang') : '',
            colorClass$: quantityFormGroup.controls.quantity.valueChanges
                .pipe(
                    startWith(null),
                    map(() => quantityFormGroup.controls.quantity.getRawValue()),
                )
                .pipe(
                    map((quantity) => {
                        if (warehouseMovementType === MaterialAmountType.MaterialAmountTypeInput) {
                            if (isNotNullOrUndefined(targetQuantity)) {
                                return quantity === targetQuantity ? 'green' : 'yellow';
                            } else {
                                return <'green' | 'yellow'>null;
                            }
                        } else {
                            return quantityWarehouse < (quantity || 0) ? 'yellow' : 'green';
                        }
                    }),
                    distinctUntilChanged(),
                ),
            warn$: quantityFormGroup.controls.materialId.valueChanges.pipe(
                startWith(null),
                map(() => !quantityFormGroup.controls.materialId.getRawValue()),
            ),
            quantityType: arl?.QuantityTypeId ? quantityTypes[arl.QuantityTypeId]?.Name : material?.QuantityTypeId ? quantityTypes[material.QuantityTypeId]?.Name : '',
        };
    }
    createMaterial(materialListItem: materialListItem) {
        const dialogRef = this.dialog.open<MaterialMainDataPopupComponent, MaterialMainDataPopupComponentDialogData>(MaterialMainDataPopupComponent, {
            ...MaterialMainDataPopupComponent.DefaultConfig,
            data: {
                MaterialId: null,
                initialEditMode: true,
                newMaterialDefaultValues: materialListItem.arlId
                    ? {
                          Name: materialListItem.name,
                          // Description: materialListItem.name,
                          ArticleNumber: materialListItem.number,
                          Amount: materialListItem.quantityFormGroup.value.quantity,
                          QuantityType: materialListItem.quantityTypeId ? { Id: materialListItem.quantityTypeId } : null,
                      }
                    : undefined,
            },
        });
        dialogRef.componentInstance.submitted.pipe(takeUntil(dialogRef.afterClosed())).subscribe((addedMaterial) => {
            materialListItem.quantityFormGroup.controls.materialId.setValue(addedMaterial);
            this.updateMaterialValues(materialListItem, addedMaterial);
            dialogRef.close();
        });
    }
    async updateMaterialValues(materialListItem: materialListItem, material: MaterialEntity) {
        const quantityTypes = await firstValueFrom(this.quantityTypes$);
        const newValues = this.getValuesFromMaterialEntity(quantityTypes, materialListItem.warehouseMovementType, material);
        materialListItem.number = newValues.number;
        materialListItem.name = newValues.name;
        materialListItem.quantityFromWarehouse = newValues.quantityFromWarehouse;
        materialListItem.searchValues = newValues.searchValues;
    }
    public async getCurrentMaterialData() {
        const data = await firstValueFrom(this.materialList$);
        return data
            .filter((item) => !item.hide && !item.disabled && item.quantityFormGroup.value.quantity && item.quantityFormGroup.value.materialId?.Id)
            .map((item) => {
                return {
                    materialId: item.quantityFormGroup.value.materialId.Id,
                    quantity: item.quantityFormGroup.value.quantity,
                    quantityTypeId: item.quantityTypeId,
                    commissionId: item.commissionId,
                    type: item.warehouseMovementType,
                    commissionAuftragsNummer: item.detectedCommissionNumber,
                };
            });
    }

    protected readonly MaterialEntity = MaterialEntity;
    pageSize$ = this._materialPageService.selectPageSize$;
}
